/**
 * Assess whether a student has enough graduated-response evidence for an EHCNA request.
 */

function countCompletedApdrCycles(student) {
  const plan = student?.apdrSupportPlan;
  if (!plan?.cycles?.length) {
    return Array.isArray(student?.apdrCycles) ? student.apdrCycles.length : 0;
  }
  return plan.cycles.filter((c) => {
    const hasRows = Array.isArray(c.rows) && c.rows.length > 0;
    const hasLegacy = c.legacyNotes || c.legacyOutcome;
    const hasReview = c.reviewDate;
    return hasReview && (hasRows || hasLegacy);
  }).length;
}

export function assessEvidenceReadiness(bundle, sessionLogCount = 0) {
  const interventions = bundle?.interventions?.length || 0;
  const studentProvisions = bundle?.studentProvisions?.length || 0;
  const provisionBreadth = interventions + studentProvisions;
  const targets = bundle?.targets?.length || 0;
  const apdrSnapshots = bundle?.apdrEvidence?.length || 0;
  const apdrCycleCount = bundle?.apdrCycleCount ?? countCompletedApdrCycles({ apdrSupportPlan: bundle?.apdrSupportPlan, apdrCycles: bundle?.apdrCycles });
  const tacMeetings = bundle?.tacMeetings?.length || 0;
  const apdrEvidence = apdrSnapshots + apdrCycleCount;
  const therapistReports = bundle?.therapistReports?.length || 0;

  let score = 0;
  const maxScore = 8;
  const gaps = [];

  if (sessionLogCount >= 10) score += 2;
  else if (sessionLogCount >= 5) score += 1;
  else gaps.push('Fewer than 5 intervention session logs on file');

  if (provisionBreadth >= 3) score += 2;
  else if (provisionBreadth >= 1) score += 1;
  else gaps.push('No interventions or student provision recorded');

  if (targets >= 3) score += 1;
  else if (targets >= 1) score += 0.5;
  else gaps.push('No termly SMART targets recorded');

  if (apdrCycleCount >= 3) score += 1.5;
  else if (apdrEvidence >= 2) score += 1;
  else if (apdrEvidence >= 1) score += 0.5;
  else gaps.push('Fewer than 3 completed APDR cycles of support');

  if (therapistReports >= 1) score += 1;
  else gaps.push('No professional (therapist/EP) report on file');

  if (tacMeetings >= 1) score += 0.5;

  const percentage = Math.round((score / maxScore) * 100);
  let strength = 'weak';
  if (percentage >= 70) strength = 'strong';
  else if (percentage >= 40) strength = 'moderate';

  const hasEHCP = Boolean(bundle?.hasEHCP);
  const canProceed = !hasEHCP && strength !== 'weak' && provisionBreadth >= 1 && sessionLogCount >= 5 && apdrCycleCount >= 1;

  return {
    score,
    maxScore,
    percentage,
    strength,
    gaps,
    counts: {
      sessionLogCount,
      interventions,
      studentProvisions,
      provisionBreadth,
      targets,
      apdrEvidence,
      apdrCycleCount,
      tacMeetings,
      therapistReports,
    },
    hasEHCP,
    canProceed,
  };
}

export function formatEvidenceSummary(bundle, readiness) {
  if (!bundle) return 'No evidence loaded yet';

  const lines = [
    `Interventions: ${readiness.counts.interventions}`,
    `Student provision: ${readiness.counts.studentProvisions}`,
    `Session logs: ${readiness.counts.sessionLogCount}`,
    `SMART targets: ${readiness.counts.targets}`,
    `APDR cycles completed: ${readiness.counts.apdrCycleCount}`,
    `Multi-agency meetings: ${readiness.counts.tacMeetings}`,
    `Professional reports: ${readiness.counts.therapistReports}`,
  ];

  if (bundle.interventions?.length) {
    lines.push('');
    lines.push('Active interventions:');
    bundle.interventions.slice(0, 5).forEach((item) => {
      const label = item.interventionName || item.interventionId || 'Intervention';
      lines.push(`  • ${label} (${item.status || 'active'})`);
    });
  }

  return lines.join('\n');
}

export function eligibilityMessage(readiness) {
  if (readiness.hasEHCP) {
    return 'Student already has an EHCP. Use the EHCP workflow on their profile instead of an EHCNA request.';
  }
  if (readiness.canProceed) {
    return `Evidence strength: ${readiness.strength}. Graduated response documentation supports proceeding with an EHCNA request.`;
  }
  if (readiness.strength === 'moderate') {
    return `Evidence strength: moderate. Consider strengthening documentation before submitting. Gaps: ${readiness.gaps.join('; ')}.`;
  }
  return `Insufficient evidence for EHCNA submission. Gaps: ${readiness.gaps.join('; ') || 'load evidence for this student first'}.`;
}

/** Map ISP apdrSupportPlan to area-first PlanDoReview for EHCNA DOCX */
export function planDoReviewFromApdrSupportPlan(apdrSupportPlan) {
  const areas = ['cognitionLearning', 'communicationInteraction', 'semh', 'sensoryPhysical'];
  const result = Object.fromEntries(areas.map((a) => [a, []]));
  if (!apdrSupportPlan?.cycles?.length) return result;

  apdrSupportPlan.cycles.forEach((cycle) => {
    const byArea = {};
    (cycle.rows || []).forEach((row) => {
      const area = row.areaOfNeed;
      if (!areas.includes(area)) return;
      if (!byArea[area]) {
        byArea[area] = {
          cycleNumber: cycle.cycleNumber,
          term: cycle.term,
          focus: cycle.assessSummary || '',
          multiAgencyAdvice: '',
          smartTargets: [],
          interventions: [],
        };
      }
      if (row.smartTarget) {
        byArea[area].smartTargets.push({
          target: row.smartTarget,
          howAchieved: (row.interventions || []).map((i) => i.name).join('; '),
          achieved: row.achieved || '',
          feedback: row.outcome || '',
        });
      }
      (row.interventions || []).forEach((i) => {
        byArea[area].interventions.push({
          name: i.name,
          frequency: i.frequency,
          duration: i.duration,
          ratio: i.ratio,
        });
      });
    });
    Object.entries(byArea).forEach(([area, cycleData]) => {
      result[area].push(cycleData);
    });
  });
  return result;
}
